import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { CSSProperties, useEffect, useState } from 'react';

type DashboardLink = {
  label: string;
  url?: string;
};

type SuperAdminDashboardIframePageProps = {
  title: string;
  links: DashboardLink[];
};

const styles = {
  page: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    pt: 0.5,
    pb: 1.5,
  },
  toolbarCard: {
    px: { xs: 1.5, md: 2 },
    py: { xs: 1, md: 1.5 },
    borderRadius: 2,
  },
  toolbar: {
    width: '100%',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'stretch', md: 'flex-end' },
    justifyContent: 'flex-end',
    gap: 2,
  },
  toolbarActions: {
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: 1.5,
  },
  select: {
    minWidth: { xs: '100%', sm: 220 },
  },
  iframeCard: {
    borderRadius: 2,
    overflow: 'hidden',
  },
  emptyState: {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
  },
};

const iframeStyle: CSSProperties = {
  width: '100%',
  minHeight: 'calc(100vh - 320px)',
  border: 'none',
  display: 'block',
};

const SuperAdminDashboardIframePage = ({ title, links }: SuperAdminDashboardIframePageProps) => {
  const availableLinks = links.filter((link): link is DashboardLink & { url: string } => Boolean(link.url?.trim()));
  const [selectedUrl, setSelectedUrl] = useState(availableLinks[0]?.url ?? '');

  useEffect(() => {
    if (!availableLinks.length) {
      setSelectedUrl('');
      return;
    }

    const hasSelectedUrl = availableLinks.some((link) => link.url === selectedUrl);

    if (!hasSelectedUrl) {
      setSelectedUrl(availableLinks[0].url);
    }
  }, [availableLinks, selectedUrl]);

  const selectedLink = availableLinks.find((link) => link.url === selectedUrl) ?? availableLinks[0];

  if (!selectedLink) {
    return (
      <Box sx={styles.page}>
        <Paper elevation={0} sx={styles.emptyState}>
          <Alert severity='info'>{`${title} dashboard is not configured for this environment.`}</Alert>
        </Paper>
      </Box>
    );
  }

  const handleSourceChange = (event: SelectChangeEvent<string>) => {
    setSelectedUrl(event.target.value);
  };

  return (
    <Box sx={styles.page}>
      <Paper elevation={0} sx={styles.toolbarCard}>
        <Stack sx={styles.toolbar}>
          <Stack sx={styles.toolbarActions}>
            {availableLinks.length > 1 && (
              <FormControl size='small' sx={styles.select}>
                <InputLabel id={`${title}-source-label`}>{`${title} Source`}</InputLabel>
                <Select
                  labelId={`${title}-source-label`}
                  label={`${title} Source`}
                  value={selectedLink.url}
                  onChange={handleSourceChange}
                >
                  {availableLinks.map((link) => (
                    <MenuItem key={link.url} value={link.url}>
                      {link.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              variant='outlined'
              color='inherit'
              href={selectedLink.url}
              target='_blank'
              rel='noreferrer'
              endIcon={<OpenInNewOutlinedIcon fontSize='small' />}
            >
              Open in new tab
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={styles.iframeCard}>
        <iframe src={selectedLink.url} style={iframeStyle} title={`${title} dashboard`}></iframe>
      </Paper>
    </Box>
  );
};

export default SuperAdminDashboardIframePage;
